import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import StandardMenu from '@wix/da-shared-react/pkg/Dropdown/MenuPreset/StandardMenu';
import {
  BiLink,
  BiEventType,
  BiDataHook,
} from '@wix/da-shared-react/pkg/biLogger';
import { TabBarProps } from '../types';
import MenuItem, { MoreMenuItem } from '../_partials/MenuItem';

import s from './HeaderDesktop.scss';

const SEARCH_MAX_VISIBLE_ITEMS = 5;

const HeaderDesktop: React.FC<TabBarProps> = ({
  activeMenuItem,
  pageType,
  isSearchPage,
  items = [],
}) => {
  const { t } = useTranslation();
  const [horizontalMenu, moreMenu] = useMemo(() => {
    // If we're on browse always show all categories.
    // On search show the pre-defined maximum and put remaining items under the "more" menu.
    if (!isSearchPage) {
      return [items, []];
    }

    const visibleItemsCount = Math.min(items.length, SEARCH_MAX_VISIBLE_ITEMS);
    const visibleMenu = items.slice(0, visibleItemsCount);
    const moreMenuItems = items.slice(visibleItemsCount);

    // If current page type is in "more" menu, swap it to visible items list
    const currentTypeItemIdx = moreMenuItems.findIndex(
      item => item.pageType === pageType
    );
    if (currentTypeItemIdx > -1) {
      const lastVisibleIndex = visibleItemsCount - 1;
      const currentTypeItem = moreMenuItems[currentTypeItemIdx];
      moreMenuItems[currentTypeItemIdx] = visibleMenu[lastVisibleIndex];
      visibleMenu[lastVisibleIndex] = currentTypeItem;
    }

    const moreMenuMenuItems = moreMenuItems.map(menuItem => ({
      link: menuItem.url,
      label: t(`browseHeader.page.${menuItem.pageType}`),
      componentClass: BiLink,
      componentProps: {
        biData: {
          evid: BiEventType.CONTENT_NAVBAR_CLICK,
          link_name: menuItem.pageType,
        },
      },
    }));

    return [visibleMenu, moreMenuMenuItems];
  }, [isSearchPage, items, pageType, t]);

  return (
    <header className={s['header']} data-hook={BiDataHook.DEVIATION_NAV}>
      {items.length > 0 && (
        <ul className={s['nav-list']}>
          {horizontalMenu.map(menuItem => {
            return (
              <li key={menuItem.pageType} className={s['nav-list__item']}>
                <MenuItem
                  menuItem={menuItem}
                  isActive={activeMenuItem === menuItem.pageType}
                  className={s['nav-list__item__link']}
                />
              </li>
            );
          })}
          {moreMenu.length > 0 && (
            <li
              className={classnames(
                s['nav-list__item'],
                s['nav-list__item--more']
              )}
            >
              <MoreMenuItem className={s['nav-list__item__link']} />
              <StandardMenu
                lightText
                id="subnav-more"
                className={s['more-list']}
                itemClassName={s['more-list__item']}
                items={moreMenu}
              />
            </li>
          )}
        </ul>
      )}
    </header>
  );
};
HeaderDesktop.displayName = 'HeaderDesktop';

export default HeaderDesktop;
